// Вкладка «Настройки»: режим регистрации, квоты (место, размер файла, число отчётов, одновременные сборки),
// срок сессии, ожидание Word, срок хранения удалённых, адреса сайта для доступа по сети; сведения о сервере
// и предупреждения о небезопасной настройке сети.
import { useEffect, useState, type ReactNode } from 'react';
import { ctx } from '../../core/ctx';
import { Btn } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { reloadInfo } from '../auth/session';
import { adminApi, type ASettings } from './adminApi';
import { LoadError, Pill, useLoad } from './common';

const REG: [ASettings['registration'], string, string, string][] = [
  ['invite', 'ticket', 'По приглашениям', 'Регистрироваться можно только по ссылке, которую вы создали во вкладке «Приглашения». Рекомендуется.'],
  ['open', 'users', 'Открытая', 'Любой, кто откроет адрес сайта, может создать учётную запись. Только для закрытой сети.'],
  ['closed', 'lock', 'Только администратор', 'Регистрации нет: учётные записи создаёте вы во вкладке «Пользователи».'],
];

function Num({ label, hint, value, onChange, unit, min, max, testid }: {
  label: string; hint?: ReactNode; value: number; onChange: (v: number) => void; unit: string; min: number; max: number; testid?: string;
}) {
  return (
    <label className="adm-set-row">
      <span className="adm-set-label">{label}{hint && <span className="adm-set-hint">{hint}</span>}</span>
      <span className="adm-num"><input type="number" min={min} max={max} value={Number.isFinite(value) ? value : ''} data-testid={testid}
        onChange={(e) => onChange(e.target.value === '' ? NaN : Number(e.target.value))} /><span className="adm-unit">{unit}</span></span>
    </label>
  );
}

function mbHint(mb: number) {
  if (!Number.isFinite(mb)) return '';
  return mb >= 1024 ? `≈ ${(mb / 1024).toFixed(mb % 1024 ? 1 : 0).replace('.', ',')} ГБ` : '';
}

export function SettingsTab() {
  const { data, error, reload, setData } = useLoad(() => adminApi.settings(), []);
  const [s, setS] = useState<ASettings | null>(null);
  const [urls, setUrls] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { if (data) { setS(data.settings); setUrls((data.settings.public_urls || []).join('\n')); } }, [data]);
  if (!s || !data) return <section className="adm-tab"><LoadError error={error} reload={reload} />{!error && <div className="adm-dim pad">Загрузка…</div>}</section>;
  const srv = data.server;
  const urlList = urls.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
  const dirty = JSON.stringify({ ...s, public_urls: urlList }) !== JSON.stringify({ ...data.settings, public_urls: data.settings.public_urls || [] });
  const up = <K extends keyof ASettings>(k: K, v: ASettings[K]) => setS({ ...s, [k]: v });
  const save = async () => {
    setErr('');
    setBusy(true);
    try {
      const r = await adminApi.saveSettings({ ...s, public_urls: urlList });
      setData({ settings: r.settings, server: r.server });
      void reloadInfo();
      ctx.toast(Object.keys(r.changed).length ? 'Настройки сохранены' : 'Изменений нет', 'ok');
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <section className="adm-tab adm-settings" data-testid="admin-settings">
      <div className="adm-card">
        <div className="adm-card-title"><Icon name="user-plus" size={17} /> Регистрация</div>
        <div className="adm-reg" role="radiogroup" aria-label="Режим регистрации">
          {REG.map(([id, icon, title, text]) => (
            <button key={id} type="button" role="radio" aria-checked={s.registration === id} className={'adm-reg-opt' + (s.registration === id ? ' is-active' : '')}
              onClick={() => up('registration', id)} data-testid={'reg-' + id}>
              <span className="adm-reg-ico"><Icon name={icon} size={18} /></span>
              <span className="adm-reg-title">{title}</span>
              <span className="adm-reg-text">{text}</span>
            </button>
          ))}
        </div>
        {s.registration === 'closed' && data.settings.registration !== 'closed' && (
          <div className="adm-warn" data-testid="reg-closed-warning"><Icon name="warn" size={16} />
            <span>Действующие ссылки-приглашения перестанут работать: по ним откроется сообщение «Регистрация закрыта».</span></div>
        )}
      </div>

      <div className="adm-grid2">
        <div className="adm-card">
          <div className="adm-card-title"><Icon name="disk" size={17} /> Квоты пользователя</div>
          <Num label="Место на диске" hint={<>на все его отчёты; {mbHint(s.quota_mb)}</>} value={s.quota_mb} onChange={(v) => up('quota_mb', v)} unit="МБ" min={1} max={1048576} testid="set-quota" />
          <Num label="Размер одного файла" hint="загрузка картинки, импорт, сохранение" value={s.max_file_mb} onChange={(v) => up('max_file_mb', v)} unit="МБ" min={1} max={256} testid="set-maxfile" />
          <Num label="Отчётов у пользователя" value={s.max_projects} onChange={(v) => up('max_projects', v)} unit="шт." min={1} max={100000} testid="set-maxprojects" />
          <Num label="Одновременных сборок" hint="у одного пользователя" value={s.max_jobs_per_user} onChange={(v) => up('max_jobs_per_user', v)} unit="шт." min={1} max={50} />
          <p className="adm-note"><Icon name="info" size={14} /> На администраторов общие пределы не действуют. Свою квоту пользователю -
            «Пользователи → ⋯ → Изменить».</p>
        </div>
        <div className="adm-card">
          <div className="adm-card-title"><Icon name="clock" size={17} /> Сеансы и очередь</div>
          <Num label="Вход без «Запомнить меня»" hint="после бездействия - войти заново" value={s.session_hours} onChange={(v) => up('session_hours', v)} unit="ч" min={1} max={336} testid="set-session" />
          <Num label="Вход с «Запомнить меня»" value={s.remember_days} onChange={(v) => up('remember_days', v)} unit="дн." min={1} max={365} />
          <Num label="Ожидание Word в очереди" hint="дольше - сборка отменяется с сообщением" value={s.queue_wait_min} onChange={(v) => up('queue_wait_min', v)} unit="мин" min={1} max={1440} />
          <Num label="Работа Word на одну сборку" hint="дольше - Word этой сборки останавливается, очередь идёт дальше" value={s.word_max_min}
            onChange={(v) => up('word_max_min', v)} unit="мин" min={1} max={240} testid="set-wordmax" />
          <Num label="Хранить удалённых пользователей" hint="столько дней их можно восстановить" value={s.user_trash_days} onChange={(v) => up('user_trash_days', v)} unit="дн." min={1} max={3650} />
        </div>
      </div>

      <div className="adm-card" data-testid="set-billing">
        <div className="adm-card-title"><Icon name="star" size={17} /> Подписка</div>
        <Num label="Новым пользователям" hint="сколько дней подписки даётся при создании; 0 - только просмотр, пока не оформят" value={s.new_user_sub_days}
          onChange={(v) => up('new_user_sub_days', v)} unit="дн." min={0} max={36500} testid="set-newsub" />
        <div className="adm-grid2">
          <div>
            <Num label="1 месяц" value={s.price_1m} onChange={(v) => up('price_1m', v)} unit="₽" min={0} max={10000000} testid="set-price-1m" />
            <Num label="3 месяца" value={s.price_3m} onChange={(v) => up('price_3m', v)} unit="₽" min={0} max={10000000} />
            <Num label="6 месяцев" value={s.price_6m} onChange={(v) => up('price_6m', v)} unit="₽" min={0} max={10000000} />
            <Num label="1 год" value={s.price_12m} onChange={(v) => up('price_12m', v)} unit="₽" min={0} max={10000000} />
          </div>
          <div>
            <Num label="1 месяц, в Telegram" hint="0 - тариф не продаётся за звёзды" value={s.stars_1m} onChange={(v) => up('stars_1m', v)} unit="⭐" min={0} max={100000} testid="set-stars-1m" />
            <Num label="3 месяца, в Telegram" value={s.stars_3m} onChange={(v) => up('stars_3m', v)} unit="⭐" min={0} max={100000} />
            <Num label="6 месяцев, в Telegram" value={s.stars_6m} onChange={(v) => up('stars_6m', v)} unit="⭐" min={0} max={100000} />
            <Num label="1 год, в Telegram" value={s.stars_12m} onChange={(v) => up('stars_12m', v)} unit="⭐" min={0} max={100000} />
          </div>
        </div>
        <label className="adm-set-row">
          <span className="adm-set-label">Бот Telegram<span className="adm-set-hint">имя без @; бот работает на сервере, задача «Бот СТО»</span></span>
          <input value={s.tg_bot} onChange={(e) => up('tg_bot', e.target.value)} spellCheck={false} data-testid="set-tgbot" />
        </label>
      </div>

      <div className="adm-card">
        <div className="adm-card-title"><Icon name="server" size={17} /> Доступ по сети</div>
        <div className="adm-server" data-testid="server-info">
          <div><span>Адрес</span><b className="mono">{srv.host}:{srv.port}</b>{srv.localOnly ? <Pill tone="ok">только этот компьютер</Pill> : <Pill tone="warn">доступен по сети</Pill>}</div>
          <div><span>HTTPS</span><b>{srv.https ? 'да' : 'нет'}</b>{srv.trustProxy && <Pill tone="info">за обратным прокси</Pill>}</div>
          <div><span>Адреса из командной строки</span><b className="mono">{srv.publicUrls.length ? srv.publicUrls.join(', ') : '—'}</b></div>
          <div><span>Папка данных</span><b className="mono">{srv.dataDir}</b></div>
        </div>
        <label className="adm-set-row adm-set-col">
          <span className="adm-set-label">Адреса сайта<span className="adm-set-hint">по одному в строке, например https://sto.example.ru или http://192.168.1.10:8765 -
            с других адресов браузер к редактору не пустят (защита Host/Origin). Сеть и HTTPS настраиваются при запуске - web\DEPLOY.md.</span></span>
          <textarea rows={3} value={urls} onChange={(e) => setUrls(e.target.value)} placeholder="https://sto.example.ru" data-testid="set-urls" spellCheck={false} />
        </label>
        {srv.warnings.map((w) => <div key={w} className="adm-warn" data-testid="server-warning"><Icon name="warn" size={16} /><span>{w}</span></div>)}
      </div>

      {err && <div className="adm-error" role="alert" data-testid="settings-error"><Icon name="error" size={16} /><span>{err}</span></div>}
      <div className={'adm-savebar' + (dirty ? ' is-dirty' : '')}>
        <span>{dirty ? 'Есть несохранённые изменения' : 'Все изменения сохранены'}</span>
        <span className="topbar-spacer" />
        <Btn label="Отменить" onClick={() => { setS(data.settings); setUrls((data.settings.public_urls || []).join('\n')); setErr(''); }} disabled={!dirty || busy} />
        <Btn icon="save" label="Сохранить" primary onClick={save} disabled={!dirty || busy} testid="settings-save" />
      </div>
    </section>
  );
}
